import * as React from 'react';
import type { UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import type { TFunction } from 'i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';
import { DisabledHintTooltip } from '@ooh/ui/components/disabled-hint-tooltip';
import { FormField } from '@ooh/ui/components/form-field';
import { Input } from '@ooh/ui/components/input';
import {
  SearchableSelectField as Select,
  type SearchableSelectFieldOption as SelectOption,
} from '@ooh/ui/components/searchable-select-field';
import type { FormValues } from '../../../../lib/media-unit-form-schema';

interface SupplierSectionProps {
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  supplierOptions: SelectOption[];
  isSuppliersLoading: boolean;
  t: TFunction<'inventory'>;
  shouldDirty?: boolean;
  /**
   * False when the caller lacks supplier.view for this unit — disables both
   * supplier inputs with a shared permission explanation and drops their
   * required indicator, since a field the caller cannot see cannot be a
   * required field for them either (field-visibility, CU-86cbgv42n). Takes
   * precedence over the unrelated `isSuppliersLoading` disable reason.
   * Defaults to true: only the edit form gates this — the create form
   * always requires a supplier and is unaffected by this change.
   */
  canEditSupplier?: boolean;
}

export function SupplierSection({
  register,
  errors,
  watch,
  setValue,
  supplierOptions,
  isSuppliersLoading,
  t,
  shouldDirty,
  canEditSupplier = true,
}: SupplierSectionProps) {
  const supplierDisabledHint = t('form.fields.supplierDisabledHint');
  return (
    <Card data-testid="section-supplier">
      <CardHeader className="pb-4">
        <CardTitle className="text-[15px]">{t('form.sections.supplier')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label={t('form.fields.supplierNumber')}
            required={canEditSupplier}
            error={errors.supplierNumber?.message}
            testId="field-supplierNumber"
          >
            <DisabledHintTooltip disabled={!canEditSupplier} hint={supplierDisabledHint}>
              <Input
                placeholder={t('form.placeholders.supplierNumber')}
                error={!!errors.supplierNumber}
                disabled={!canEditSupplier}
                data-testid="input-supplierNumber"
                {...register('supplierNumber')}
              />
            </DisabledHintTooltip>
          </FormField>

          <FormField
            label={t('form.fields.supplier')}
            required={canEditSupplier}
            error={errors.supplierId?.message}
            testId="field-supplier"
          >
            <DisabledHintTooltip disabled={!canEditSupplier} hint={supplierDisabledHint}>
              <Select
                options={supplierOptions}
                value={watch('supplierId') ?? ''}
                onChange={(v) =>
                  setValue('supplierId', v, shouldDirty ? { shouldDirty: true } : undefined)
                }
                placeholder={
                  isSuppliersLoading
                    ? t('form.loadingPlaceholder')
                    : t('form.placeholders.supplier')
                }
                disabled={!canEditSupplier || isSuppliersLoading}
                error={!!errors.supplierId}
                data-testid="select-supplier"
              />
            </DisabledHintTooltip>
          </FormField>
        </div>
      </CardContent>
    </Card>
  );
}
